import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";

// Session cache — the enum/options endpoints don't change per user click.
let _cache = null;

/**
 * useFilterOptions — one-shot loader for filter dropdown data.
 *
 * Merges `/api/partners/enums` (static enums) with
 * `/api/partners/filter-options` (dynamic: companies, agents, sources).
 * Cached in-module for the tab's lifetime.
 *
 * Returns `{ options, loading, refresh }`.
 */
export function useFilterOptions() {
  const { token } = useAuth();
  const [options, setOptions] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [enumRes, optRes] = await Promise.all([
        axios.get(`${API}/partners/enums`, { headers }),
        axios.get(`${API}/partners/filter-options`, { headers }),
      ]);
      _cache = { ...enumRes.data, ...optRes.data };
      setOptions(_cache);
    } catch (e) {
      // Non-fatal: pages can still render with empty option lists.
       
      console.warn("useFilterOptions failed", e?.response?.status);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!_cache && token) load();
  }, [token, load]);

  return { options: options || {}, loading, refresh: () => { _cache = null; load(); } };
}

export default useFilterOptions;
